import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { formatDuration, intervalToDuration } from "date-fns";
import CopyToBufferButton from "@entrilia/shared/components/inputs/CopyToBufferButton";
import MarkdownPreview from "@entrilia/shared/components/markdown/MarkdownPreview";
import MultilineTooltipTitle from "@entrilia/shared/components/MultilineTooltipTitle";
import { formatNumberWithClosestFactor } from "@entrilia/shared/utilities/formatters";
import type { CopilotAgentMessage } from "../../../../api/copilotAgentTypes";
import AgentIcon from "../AgentIcon";
import ReasoningBlock from "./ReasoningBlock";

interface Props {
  message: CopilotAgentMessage;
  agentSlug: string;
}

const MessageBubble = ({ message, agentSlug }: Props) => {
  const isUser = message.role === "user";

  const cachedInputTokens = message.usage?.cachedInputTokens;
  const hasCachedInputTokens = cachedInputTokens !== undefined && cachedInputTokens > 0;

  if (isUser) {
    return (
      <Stack direction="row" sx={{ justifyContent: isUser ? "flex-end" : "flex-start", mb: 1.5 }}>
        <Box sx={{ maxWidth: "80%" }}>
          <Paper variant="outlined" sx={{ px: 2, py: 1, bgcolor: "info.main", color: "info.contrastText" }}>
            <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{message.content}</Typography>
          </Paper>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1.5} sx={{ justifyContent: "flex-start", alignItems: "flex-start", mb: 1.5 }}>
      <AgentIcon slug={agentSlug} size={56} />
      <Box sx={{ maxWidth: "80%" }}>
        {message.reasoning && <ReasoningBlock reasoning={message.reasoning} isStreaming={!message.usage} />}

        {message.content && (
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <Paper variant="outlined" sx={{ px: 2, py: 1, minHeight: "4rem" }}>
              <MarkdownPreview content={message.content.trim()} />
            </Paper>
            <CopyToBufferButton textToCopy={message.content.trim()} />
          </Stack>
        )}

        {message.usage && (
          <Tooltip
            title={
              <MultilineTooltipTitle
                lines={[
                  `${message.usage.inputTokens.toLocaleString()} input tokens`,
                  ...(hasCachedInputTokens ? [`${cachedInputTokens.toLocaleString()} cached input tokens`] : []),
                  `${message.usage.outputTokens.toLocaleString()} output tokens`,
                ]}
              />
            }
          >
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: "inline-block" }}>
              ran for {formatDuration(intervalToDuration({ start: 0, end: message.usage.durationMs }))} · ↓{" "}
              {formatNumberWithClosestFactor(message.usage.inputTokens, { maximumFractionDigits: 1 })}
              {hasCachedInputTokens && (
                <> · cached {formatNumberWithClosestFactor(cachedInputTokens, { maximumFractionDigits: 1 })}</>
              )}{" "}
              · ↑ {formatNumberWithClosestFactor(message.usage.outputTokens, { maximumFractionDigits: 1 })}
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Stack>
  );
};

export default MessageBubble;
